import React, { useEffect, useState } from 'react';
import './style.scss';
import { ProductCard } from 'views/components/molecules/product';
import { ContentHeader } from 'views/components/molecules/contentHeader';
import { IconColor, IconSize, SvgIcon } from 'views/components/atoms/svgIcon';
import { IconName, findIcon } from 'views/utils/handleIcon';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ApplySymbolMutationVariables,
  GetSymbolInfoQueryVariables,
  useApplySymbolMutation,
} from 'views/utils/graphql/graphql';
import { useGetSymbolDetail } from './hooks/useGetSymbolDetail';
import { formatThousand } from 'views/utils/calc/formatThousand';
import { operationDatePlanCalc } from 'views/utils/calc/operationDatePlan';
import { ModalBase, ModalStyle } from 'views/components/atoms/modal';
import { Toast, ToastMessageLevel } from 'views/components/atoms/toast';
import { Button, ButtonSize, FillType } from 'views/components/atoms/button';
import { useToast } from 'views/components/atoms/toast/hook';
import useOnceSubmit from 'views/utils/useOnceSubmit';
import { useGlobalLoadingHandler } from 'views/components/organisms/globalLoading/hook';
import { InputConsent } from 'views/components/molecules/establishes/inputConsent';
import { InputAdvisability } from 'views/components/molecules/establishes/inputAdvisability';
import { FinancialIntermediaryStrs, findFinancialIntermediaryId } from 'views/utils/accountEstablish';
import { InputChoices } from 'views/components/molecules/establishes/inputChoices';
import { InputLetter } from 'views/components/molecules/establishes/inputLetter';
import { useGetAccountInfo } from 'views/components/organisms/centerNav/hooks/useGetAccountInfo';
import { checkHeaderPath } from 'views/utils/checkHeaderPath';
import { ShowHeaderType } from 'views/constants/enums/ShowHeaderType';
import { RefundType } from 'views/constants/enums/Order';

export const ProductDetail = () => {
  // Routerのパラメーターを取得する
  const { symbol } = useParams<{ symbol: string }>();

  const getNewSymbolDetailVariables = () => {
    const variables: GetSymbolInfoQueryVariables = {
      symbol: symbol,
    };
    return variables;
  };

  const { symbolDetail, setSymbolDetailVariables } = useGetSymbolDetail(getNewSymbolDetailVariables());
  const data = symbolDetail.data?.GetSymbolInfo || null;

  const [quantity, setQuantity] = useState<number>(1);

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newAmount = parseInt(event.target.value);
    if (!isNaN(newAmount) && newAmount > 0) {
      setQuantity(newAmount);
    }
  };
  const handleDecrease = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };
  const handleIncrease = () => {
    setQuantity(quantity + 1);
  };

  const [isCheck, setIsCheck] = useState<boolean>(false);
  const [isCheck2, setIsCheck2] = useState<boolean>(false);

  const [isLinkOpen, setIsLinkOpen] = useState<boolean>(false);
  const [isLinkOpen2, setIsLinkOpen2] = useState<boolean>(false);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isOpen2, setIsOpen2] = useState<boolean>(false);

  const getNewVariables = () => {
    return {};
  };

  const location = useLocation();
  const { accountInfo } = useGetAccountInfo(
    getNewVariables(),
    checkHeaderPath(location.pathname) !== ShowHeaderType.Info,
  );

  // 抽選落選時の取り扱い
  const [lotteryFailureRefundType, setLotteryFailureRefundType] = useState<boolean | undefined>();

  // 口座開設時の勧誘有無
  const invitationAccountOpening: boolean = accountInfo.data?.GetAccountInfo?.FinancialIntermediaryInvitation ?? false;

  // 仲介業者情報系
  const [financialIntermediaryInvitation, setFinancialIntermediaryInvitation] = useState<boolean>();
  const [financialIntermediaryExplanation, setFinancialIntermediaryExplanation] = useState<boolean>();
  const [financialIntermediary, setFinancialIntermediary] = useState<string>();
  const [managerCode, setManagerCode] = useState<string>();
  const [isFPGSecExplained, setIsFPGSecExplained] = useState<boolean>();

  const [applySymbolData, applySymbol] = useApplySymbolMutation();
  const applySymbolSubmit = useOnceSubmit<void>(
    {
      onSubmit: async () => {
        if (!symbol) return;
        const param: ApplySymbolMutationVariables = {
          input: {
            Symbol: symbol,
            Quantity: quantity,
            RefundType: lotteryFailureRefundType ? RefundType.預り金 : RefundType.返金,
            IntermediaryData: {
              FinancialIntermediaryInvitation: financialIntermediaryInvitation ?? false,
              FinancialIntermediaryExplanation: financialIntermediaryExplanation,
              FinancialIntermediary: findFinancialIntermediaryId(String(financialIntermediary)),
              ManagerCode: managerCode,
              FPGSecIntermediaryExplanation: isFPGSecExplained,
            },
          },
        };
        await applySymbol(param);
        return;
      },
      isLoadingForceCenter: true,
    },
    [symbol, quantity, applySymbol],
  );

  const navigate = useNavigate();

  const { toastProps, showToast, hideToast } = useToast();

  const { showLoading, hideLoading, setLoadingCenter } = useGlobalLoadingHandler();

  useEffect(() => {
    setLoadingCenter(true);
    if (symbolDetail.fetching) {
      showLoading();
    } else {
      hideLoading();
    }
  }, [symbolDetail]);

  useEffect(() => {
    if (applySymbolData.fetching) return;
    if (!applySymbolData.data) return;

    if (applySymbolData.data.ApplySymbol?.Code === 0) {
      setIsOpen(false);
      setIsOpen2(true);
    } else if (applySymbolData.data.ApplySymbol?.Code !== 0) {
      setIsOpen(false);
      const msgList = applySymbolData.data.ApplySymbol?.AdditionalMessage;
      const msg = msgList?.map((x, i) => (i < msgList.length - 1 ? '・' + x : x)).join('\n\n');
      showToast(msg ?? applySymbolData.data.ApplySymbol?.Message ?? '申込に失敗しました', ToastMessageLevel.Error);
    }
  }, [applySymbolData]);

  // 仲介業者の入力バリデーション
  function checkIntermediary(
    invitationAccountOpening: boolean,
    financialIntermediaryInvitation: boolean | undefined,
    financialIntermediaryExplanation: boolean | undefined,
    financialIntermediary: string | undefined,
    managerCode: string | undefined,
    isFPGSecExplained: boolean | undefined,
  ) {
    if (financialIntermediaryInvitation === undefined) {
      return { result: false, message: '株式会社ＦＰＧからの説明有無を選択してください' };
    }

    // 口座開設時と申込時どちらも勧誘あり
    if (financialIntermediaryInvitation === true && invitationAccountOpening === true) {
      return { result: true, message: '' };
    }

    // 仲介業者情報の入力が必要か
    const isNeedIntermediary = financialIntermediaryInvitation && !invitationAccountOpening;
    if (!isNeedIntermediary && isFPGSecExplained === undefined) {
      return { result: false, message: '株式会社ＦＰＧ証券からの説明有無を選択してください' };
    }

    if (isNeedIntermediary) {
      if (financialIntermediaryExplanation === undefined || financialIntermediaryExplanation === false) {
        return {
          result: false,
          message:
            '「金融商品仲介業に関する明示事項」の交付を受けた場合、✓を入力してください（該当しない場合は、申込できません）',
        };
      }
      if (financialIntermediary === undefined) {
        return { result: false, message: '説明を受けた業者名を選択してください' };
      }
      if (managerCode === undefined || managerCode.length !== 6) {
        return { result: false, message: '担当者コードを6桁で入力してください' };
      }
    }
    return { result: true, message: '' };
  }

  return (
    <section className="outletWrapper">
      <div className="centerContent">
        <ProductCard
          title={data?.SymbolName || ''}
          image={data?.ImageUrl || ''}
          TradeStartDate={data?.TradeStartDate || ''}
          TradeEndDate={data?.TradeEndDate || ''}
          description={data?.Overview || ''}
          details={[
            {
              title: '一口',
              item: formatThousand(data?.PricePerUnit || 0) || '',
              frontUnit: '',
              rearUnit: '万円',
            },
            {
              title: '予想分配率',
              item: data?.ExpectedInterestRate.toString() || '',
              frontUnit: '年',
              rearUnit: '%',
            },
            {
              title: '予定運用期間',
              item: operationDatePlanCalc(data?.OperationStartDate || '', data?.OperationEndDate || ''),
              frontUnit: '約',
              rearUnit: '',
            },
          ]}
          tradable={data?.Tradable || false}
        />
        <div className="detailWrapper">
          <ContentHeader title={'銘柄概要'} />
          <span className="detailWrapper_description">{data?.Explanation || ''}</span>
        </div>
      </div>
      <div className="rightContent">
        <div className="rightContent_wrapper">
          <ContentHeader title="申込口数" />
          <div className="requestCount">
            <button className="requestCount_spin sub" onClick={handleDecrease}>
              －
            </button>
            <input className="requestCount_counter" type="number" value={quantity} onChange={handleAmountChange} />
            <button className="requestCount_spin add" onClick={handleIncrease}>
              ＋
            </button>
          </div>
        </div>

        <div className="rightContent_wrapper">
          <ContentHeader title="申込金額" />
          <div className="requestAmount">
            <span className="requestAmount_value">{(quantity * (data?.PricePerUnit || 0)).toLocaleString()}</span>
            <span className="requestAmount_unit">円</span>
          </div>
        </div>

        {/* TODO:PDFのリンクをクリックしないとチェックボックスは押せない */}
        <div className="rightContent_wrapper">
          <ContentHeader title="確認事項" />
          <div className="requestMatter">
            <span className="requestMatter_description">
              ※「目論見書」及び「契約締結前交付書面」のURLをクリックすることでチェックできるようになります
            </span>
            <span className="requestMatter_description">
              ※書面のダウンロードが必要な場合はブラウザに表示した後、ブラウザの機能で保存可能です
            </span>
            <ConfirmCheck
              href={data?.ProspectusUrl || ''}
              pdfLabel="目論見書"
              unitLabel="を読み、その内容を理解しました"
              checked={isCheck}
              disabled={!isLinkOpen}
              onChange={() => setIsCheck(!isCheck)}
              onPDFLinkOpen={() => setIsLinkOpen(true)}
            />
            <ConfirmCheck
              href={data?.PreContractDocumentUrl || ''}
              pdfLabel="契約締結前交付書面"
              unitLabel="を読み、その内容を理解しました"
              checked={isCheck2}
              disabled={!isLinkOpen2}
              onChange={() => setIsCheck2(!isCheck2)}
              onPDFLinkOpen={() => setIsLinkOpen2(true)}
            />
            <InputAdvisability
              title="配分出来なかった金額のお取り扱い方法"
              name="refundType"
              agreeText="FPG証券お客様口座にて預り金として管理"
              denialText="ご登録銀行口座への返金"
              agreeValue="true"
              denialValue="false"
              checked={lotteryFailureRefundType}
              onChange={(checked) => {
                setLotteryFailureRefundType(checked);
              }}
              isEnabled={false}
              errorText={'error'}
            />
            <InputAdvisability
              title="本商品の説明を株式会社ＦＰＧから受けましたか？"
              name="agency"
              agreeText="はい"
              denialText="いいえ"
              agreeValue="true"
              denialValue="false"
              checked={financialIntermediaryInvitation}
              onChange={(checked) => {
                setFinancialIntermediaryInvitation(checked);
              }}
              isEnabled={false}
              errorText={'error'}
            />
            {financialIntermediaryInvitation && !invitationAccountOpening ? (
              <>
                <div className="requestMatter_financialIntermediaryInvitation">
                  <h1>勧誘業者情報を入力してください</h1>
                  <span>
                    私を株式会社FPGに紹介した紹介者（その継承人を含む）がいる場合、紹介状況等取引に関する情報が株式会社FPGを通して紹介者へ提供されることを同意します
                  </span>
                </div>
                <InputConsent
                  title="口座開設申込の説明を受けた際、あるいは今回の商品説明を受けた際、「金融商品仲介業に関する明示事項」の交付を受けましたか？"
                  name="invitation"
                  checkText="交付を受けた"
                  isChecked={financialIntermediaryExplanation ?? false}
                  setIsChecked={(e) => setFinancialIntermediaryExplanation(e.target.checked)}
                  isEnable={false}
                />
                <InputChoices
                  title="説明を受けた業者名"
                  selectedOption={financialIntermediary ?? ''}
                  setSelectedOption={(selectedOption) => setFinancialIntermediary(selectedOption)}
                  options={FinancialIntermediaryStrs}
                  isEnabled={true}
                />
                <InputLetter
                  inputType="number"
                  title="担当者コード"
                  inputValue={managerCode}
                  setInputValue={(e) => setManagerCode(e.target.value)}
                  isEnable={true}
                  placeholder="担当者コードを入力してください"
                />
              </>
            ) : (
              financialIntermediaryInvitation === false && (
                <>
                  <InputAdvisability
                    title="本商品の説明を株式会社ＦＰＧ証券から受けましたか？"
                    name="isFpg"
                    agreeText="はい"
                    denialText="いいえ"
                    agreeValue="true"
                    denialValue="false"
                    checked={isFPGSecExplained}
                    onChange={(checked) => {
                      setIsFPGSecExplained(checked);
                    }}
                    isEnabled={false}
                    errorText={'error'}
                  />
                </>
              )
            )}
            <span className="requestMatter_description">ご説明が必要な場合は、お問い合わせ画面からご連絡ください</span>
          </div>
        </div>

        <Button
          label={data?.Tradable ? '申込内容の確認へ' : '募集前のためお申込みできません'}
          onClick={() => {
            const targetPDFList = [];
            if (!isCheck || !isLinkOpen) {
              targetPDFList.push('目論見書');
            }
            if (!isCheck2 || !isLinkOpen2) {
              targetPDFList.push('契約締結前交付書面');
            }
            if (targetPDFList.length !== 0) {
              const pdfMsg = targetPDFList.join('・');
              showToast(`${pdfMsg}の内容を確認し、チェックを付けてください`, ToastMessageLevel.Error);
              return;
            }

            if (lotteryFailureRefundType === undefined) {
              showToast(
                `配分出来なかった金額のお取り扱い方法を選択してください`,
                ToastMessageLevel.Error,
              );
              return;
            }

            const { result, message } = checkIntermediary(
              invitationAccountOpening,
              financialIntermediaryInvitation,
              financialIntermediaryExplanation,
              financialIntermediary,
              managerCode,
              isFPGSecExplained,
            );
            if (!result) {
              showToast(message, ToastMessageLevel.Error);
              return;
            }

            hideToast();
            setIsOpen(true);
          }}
          fillType={FillType.primaryFill}
          buttonSize={ButtonSize.sizeM}
          disabled={!data?.Tradable}
        />
      </div>
      <ModalBase
        isOpen={isOpen}
        onRequestClose={() => setIsOpen(false)}
        modalStyle={ModalStyle.confirmModal}
        children={
          <div className="modalWrapper">
            <section>
              <ContentHeader title="申込口数" />
              <div className="confirmItem">
                <span className="confirmItem_value">{quantity.toLocaleString()}</span>
                <span className="confirmItem_unit">口</span>
              </div>
              <ContentHeader title="申込金額" />
              <div className="confirmItem">
                <span className="confirmItem_value">{(quantity * (data?.PricePerUnit || 0)).toLocaleString()}</span>
                <span className="confirmItem_unit">円</span>
              </div>
            </section>

            <div className="confirmCaution">
              <div>＜ご注意＞</div>
              <div>・申込後の訂正、取消しはできません</div>
              <div>
                ・目論見書および契約締結前交付書面に記載の投資リスクなどにより、場合によっては投資金額の全額または一部を回収できないことがあります
              </div>
              <div>
                ・取得の申込をする金融商品は預金等ではなく、預金保険法第五十三条に規定する保険金の支払い（いわゆる「預金保険機構」による保険金の支払い）の対象とはなりません
              </div>
              <div>・抽選割り当ての結果によっては、受益証券を取得できない場合があります</div>
            </div>

            <Button
              label="内容を確認して申込む"
              onClick={applySymbolSubmit}
              fillType={FillType.primaryFill}
              buttonSize={ButtonSize.sizeM}
            />
          </div>
        }
      />
      <ModalBase
        isOpen={isOpen2}
        modalStyle={ModalStyle.completeModal}
        children={
          <div className="modalWrapper">
            <span className="finishLabel">申込が完了しました</span>
            <Button
              label="閉じる"
              onClick={() => {
                setIsOpen2(false);
                // productListへ遷移
                navigate('/productList');
              }}
              fillType={FillType.primaryFill}
              buttonSize={ButtonSize.sizeM}
            />
          </div>
        }
      />
      <Toast {...toastProps} />
    </section>
  );
};

// 銘柄詳細の申込前に利用
type ConfirmCheckProps = {
  href: string;
  pdfLabel: string;
  unitLabel: string;
  checked: boolean;
  disabled: boolean;
  onChange: () => void;
  onPDFLinkOpen: () => void;
};

export const ConfirmCheck = (props: ConfirmCheckProps) => {
  const { href, pdfLabel, unitLabel, checked, disabled, onChange, onPDFLinkOpen } = props;
  const icon = findIcon(IconName.PdfFile);
  return (
    <div className="confirmCheck">
      <input
        className="confirmCheck_button"
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <div className="confirmCheck_labels">
        <SvgIcon icon={icon} size={IconSize.icon_m} color={IconColor.gray} />
        <span>
          <a href={href} target="_blank" rel="noreferrer noopener" onClick={onPDFLinkOpen}>
            {pdfLabel}
          </a>
          {unitLabel}
        </span>
      </div>
    </div>
  );
};
